<template>
	<v-app
		class="app-default-layout"
	>

		<v-navigation-drawer
			rail
			permanent
			drawer
			touchless
			class="h-screen main-sidebar"
		>

			<div class="d-flex flex-column justify-center align-center fill-height overflow-auto">

				<template
					v-for="action in actions"
					:key="`action-${action.key}`"
				>
					<div
						v-if="action.onTrackLogo"
						class="logo"
					/>

					<v-spacer
						v-else-if="action.divider"
					/>

					<v-tooltip
						v-else
						:text="action.tooltipText"
						class="navigation-tooltip"
					>
						<template v-slot:activator="{ props }">

							<ao-icon-btn
								v-bind="props"
								:to="action.to"
								:href="action.href"
								:target="action.target"
								:iconName="action.iconName"
								:mdi="action.mdi"
								:color="action.sidebarKey === leftSidebarName ? 'primary' : null"
								:badge="action.showBadge"
								size="default"
								variant="flat"
								badgeOverlap
								badgeColor="error"
								class="mr-1 mb-2"
								@click="changeLeftSidebar(action.sidebarKey ?? null)"
							/>

						</template>
					</v-tooltip>
				</template>

			</div>
		</v-navigation-drawer>

		<v-navigation-drawer
			:modelValue="!!leftSidebarName"
			:width="268"
			:temporary="sidebarTemporary"
			disable-route-watcher
			@update:modelValue="changeLeftSidebar(null)"
		>
			<span
				class="sidebar-temporary-switcher"
			>
				<ao-icon-btn
					size="12"
					iconName="slide"
					variant="plain"
					@click="onClickSidebarTemporarySwitcher"
				/>
			</span>

			<div class="sidebar-content h-100 py-4 overflow-auto">

				<SidebarCompanyMain
					v-if="leftSidebarName === SIDEBAR_KEYS.MENU"
					@click:link="onClickSidebarLink"
				/>

				<SidebarFavorites
					v-else-if="leftSidebarName === SIDEBAR_KEYS.FAVORITES"
				/>

				<SidebarNotifications
					v-else-if="leftSidebarName === SIDEBAR_KEYS.NOTIFICATIONS"
				/>

				<SidebarDev
					v-else-if="leftSidebarName === SIDEBAR_KEYS.DEV"
				/>

				<SidebarReferenceMaterials
					v-else-if="leftSidebarName === SIDEBAR_KEYS.REFERENCE_MATERIALS"
				/>

				<SidebarCompanySettings
					v-else-if="leftSidebarName === SIDEBAR_KEYS.SETTINGS"
				/>

				<SidebarProfileSettings
					v-else-if="leftSidebarName === SIDEBAR_KEYS.PROFILE_SETTINGS"
				/>

			</div>

		</v-navigation-drawer>

		<slot/>

	</v-app>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { useUserStore } from '@/stores/userStore'
import { useCompanyStore } from '@/stores/companyStore'
import { useCompanyNotificationStore } from '@/stores/companyNotificationStore'

import SidebarCompanyMain from '@/components/base/LeftSidebar/SidebarCompanyMain.vue'
import SidebarFavorites from '@/components/base/LeftSidebar/SidebarFavorites.vue'
import SidebarNotifications from '@/components/base/LeftSidebar/SidebarNotifications.vue'
import SidebarDev from '@/components/base/LeftSidebar/SidebarDev.vue'
import SidebarReferenceMaterials from '@/components/base/LeftSidebar/SidebarReferenceMaterials.vue'
import SidebarCompanySettings from '@/components/base/LeftSidebar/SidebarCompanySettings.vue'
import SidebarProfileSettings from '@/components/base/LeftSidebar/SidebarProfileSettings.vue'

import {
	clientStorageLocal,
	LOCAL_STORAGE_APP_SIDEBAR_TEMPORARY,
} from '@ao/utils'

const { locale, t: $t } = useI18n()

const SIDEBAR_KEYS = {
	MENU: 'menu',
	FAVORITES: 'favorites',
	NOTIFICATIONS: 'notifications',
	DEV: 'dev',
	REFERENCE_MATERIALS: 'referenceMaterials',
	SETTINGS: 'settings',
	PROFILE_SETTINGS: 'profileSettings',
}

const userStore = useUserStore()
const {
	userHaveAccessToCompany,
	selectedCompanyId,
} = storeToRefs(userStore)

const companyStore = useCompanyStore()
const {
	companyActiveLicense,
} = storeToRefs(companyStore)

const companyNotificationStore = useCompanyNotificationStore()
const {
	companyNotificationsCount,
} = storeToRefs(companyNotificationStore)

const isProductionMode = computed(() => {
	const productionModes = ['stage', 'prod']
	return productionModes.includes(import.meta.env.VITE_APP_ENV_MODE)
})

const wikiLink = computed(() => {
	const domain = import.meta.env.VITE_APP_WIKI_DOMAIN
	if (!domain || domain === 'null') return null

	const wikiLocales = ['uk', 'en', 'kk', 'ru']
	return `${domain}${wikiLocales.includes(locale.value) ? locale.value : 'en'}/home`
})

const companyActions = computed(() => {
	if (selectedCompanyId.value && userHaveAccessToCompany.value && companyActiveLicense.value) {
		const _companyActions = [
			// {
			// 	key: 'home',
			// 	tooltipText: $t('Homepage'),
			// 	to: { name: 'CompanyDashboardPage', params: { companyId: selectedCompanyId.value } },
			// 	sidebarKey: undefined,
			// 	iconName: 'dashboardView',
			// },
			{
				key: 'menu',
				tooltipText: $t('Menu'),
				to: undefined,
				sidebarKey: SIDEBAR_KEYS.MENU,
				iconName: 'mainMenu',
			},
			{
				key: 'favorites',
				tooltipText: $t('Favorites'),
				to: undefined,
				sidebarKey: SIDEBAR_KEYS.FAVORITES,
				iconName: 'favorites',
			},
			{
				key: 'notifications',
				tooltipText: $t('Notifications'),
				to: undefined,
				sidebarKey: SIDEBAR_KEYS.NOTIFICATIONS,
				iconName: 'notifications',
				showBadge: !!companyNotificationsCount.value,
			},
			// {
			// 	key: 'search',
			// 	tooltipText: $t('Search'),
			// 	to: undefined,
			// 	sidebarKey: 'search',
			// 	iconName: 'search',
			// },
			isProductionMode.value ? null : {
				key: 'dev',
				tooltipText: 'Development',
				to: undefined,
				sidebarKey: SIDEBAR_KEYS.DEV,
				iconName: 'dev',
			},
			{
				key: 'divider',
				divider: true,
			},
			{
				key: 'referenceMaterials',
				tooltipText: $t('menu.directories'),
				to: undefined,
				sidebarKey: SIDEBAR_KEYS.REFERENCE_MATERIALS,
				iconName: 'referenceMaterials',
			},
			{
				key: 'settings',
				tooltipText: $t('Company settings'),
				to: undefined,
				sidebarKey: SIDEBAR_KEYS.SETTINGS,
				iconName: 'settings',
			},
		]
		return _companyActions.filter(Boolean)
	}
	return [
		{
			key: 'divider',
			divider: true,
		},
	]
})

const actions = computed(() => {
	const _actions = [
		{
			key: 'onTrackLogo',
			onTrackLogo: true,
		},
		...companyActions.value,
		// {
		// 	key: 'onlineSupport',
		// 	tooltipText: $t('Online support'),
		// 	to: undefined,
		// 	sidebarKey: 'onlineSupport',
		// 	iconName: 'onlineSupport',
		// },
		wikiLink.value ? {
			key: 'wiki',
			tooltipText: $t('Reference materials'),
			to: undefined,
			sidebarKey: undefined,
			iconName: 'textDescription',
			href: wikiLink.value,
			target: '_blank',
		} : null,
		{
			key: 'profileSettings',
			tooltipText: $t('User Profile'),
			to: undefined,
			sidebarKey: SIDEBAR_KEYS.PROFILE_SETTINGS,
			iconName: 'mdi-account-circle-outline',
			mdi: true,
		},
	].filter(Boolean)
	return _actions
})

const sidebarTemporary = ref(false)

const onClickSidebarTemporarySwitcher = () => {
	sidebarTemporary.value = !sidebarTemporary.value
	clientStorageLocal.add(LOCAL_STORAGE_APP_SIDEBAR_TEMPORARY, sidebarTemporary.value)
	const currentSidebarKey = leftSidebarName.value
	leftSidebarName.value = null
	setTimeout(() => {
		leftSidebarName.value = currentSidebarKey
	}, 200)
}

const leftSidebarName = ref(null)

const onClickSidebarLink = () => {
	// if (sidebarTemporary.value) {
	// 	setTimeout(() => {
	// 		if (leftSidebarName.value !== null) leftSidebarName.value = null
	// 	}, 300)
	// }
}

const closeSidebar = () => {
	leftSidebarName.value = null
	setTimeout(() => {
		window.dispatchEvent(new Event('resize'))
	}, 300)
}

const changeLeftSidebar = (name) => {
	if (name === null || leftSidebarName.value === name) {
		closeSidebar()
	} else {
		leftSidebarName.value = name
	}
}

onMounted(() => {
	sidebarTemporary.value = clientStorageLocal.get(LOCAL_STORAGE_APP_SIDEBAR_TEMPORARY, true)
})
</script>

<style lang="scss">
.app-default-layout {

	.main-sidebar {
		padding-top: 1.5rem;
		padding-bottom: 1rem;
	}

	.logo {
		width: 2.25rem;
		height: 2.25rem;
		margin-bottom: 1rem;
		background-image: url('/logo.svg');
		background-size: cover;
		background-repeat: no-repeat;
		background-position: center center;
	}

	.sidebar-temporary-switcher {
		position: absolute;
		top: 0.25rem;
		right: 0.75rem;
	}

}

.navigation-tooltip.v-tooltip {
	.v-overlay__content {
		position: relative;
		background: #3f464e !important;

		&:after {
			content: '';
			position: absolute;
			top: 50%;
			left: 0;
			height: 8px;
			width: 8px;
			background: inherit;
			transform: translate(-50%, -50%) rotate(45deg);
		}
	}
}
</style>
